import dayjs from 'dayjs';

import type { PlatformScheduledPost } from '@/apis/scheduler';

/**
 * Dev-only fixtures for the shared calendar posts endpoint (sc-201955).
 *
 * Link metadata is served by the real scheduler-backend endpoint (sc-201952);
 * the public shared-posts read endpoint is still in development, so dev builds
 * resolve these post fixtures instead of hitting the network. The shape mirrors
 * the agreed contract post-camelization (what `createDashHttpClient` would hand
 * back), so swapping to the real endpoint is a no-op for callers.
 *
 * Media payloads are trimmed copies of real sunny.today (brand 144) scheduled
 * posts so thumbnails and video chips render like production data. Posts are
 * mirrored across brand 144 and demo brand 2000 so multi-brand links exercise
 * the recipient brand filter.
 */

const MOCK_BRAND_ID = 144;

const MOCK_POST_BRAND_IDS = [MOCK_BRAND_ID, 2000];

const MOCK_LATENCY_MS = 300;

const MEDIA = {
  instagramVideo: {
    id: 679857219,
    brandId: MOCK_BRAND_ID,
    type: 'VIDEO',
    source: 'AI_GENERATION',
    sourceType: 'OWNED',
    createdAt: '2026-06-02T14:30:28+00:00',
    updatedAt: '2026-06-02T14:30:32+00:00',
    video: {
      duration: 8.0,
      frameRate: 24.0,
      sizes: {
        original: {
          height: 720,
          size: 2661503,
          url: 'https://cdn.dashsocial.com/media/original/1780410625.385973532662.mp4',
          width: 1280,
        },
        originalConverted: {
          height: 720,
          size: 2661696,
          url: 'https://cdn.dashsocial.com/media/full/1780410631.98963543292.mp4',
          width: 1280,
        },
      },
      thumbnails: {
        mediumSquare: {
          height: 640,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9kYXNoaHVkc29uLXN0YXRpYy5zMy5hbWF6b25hd3MuY29tL21lZGlhL3ZpZGVvX2ZyYW1lcy8xNzgwNDEwNjI5XzE4OTY0Ny5qcGc=.jpg?w=640&h=640&fit=cover',
          width: 640,
        },
        originalConverted: {
          height: 720,
          size: 77390,
          url: 'https://cdn.dashsocial.com/media/full/1780410626.183166719506.jpeg',
          width: 1280,
        },
        small: {
          height: 400,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9kYXNoaHVkc29uLXN0YXRpYy5zMy5hbWF6b25hd3MuY29tL21lZGlhL3ZpZGVvX2ZyYW1lcy8xNzgwNDEwNjI5XzE4OTY0Ny5qcGc=.jpg?h=400',
          width: 711,
        },
        smallSquare: {
          height: 400,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9kYXNoaHVkc29uLXN0YXRpYy5zMy5hbWF6b25hd3MuY29tL21lZGlhL3ZpZGVvX2ZyYW1lcy8xNzgwNDEwNjI5XzE4OTY0Ny5qcGc=.jpg?w=400&h=400&fit=cover',
          width: 400,
        },
      },
    },
  },
  catImage: {
    id: 678025895,
    brandId: MOCK_BRAND_ID,
    type: 'IMAGE',
    source: 'AI_GENERATION',
    sourceType: 'OWNED',
    createdAt: '2026-05-28T13:45:15+00:00',
    updatedAt: '2026-05-28T13:45:18+00:00',
    image: {
      sizes: {
        mediumSquare: {
          height: 640,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9jZG4uZGFzaHNvY2lhbC5jb20vbWVkaWEvZnVsbC8xNzc5OTc1OTE0LjE2Nzg0NDU4OTgzNi5qcGVn.jpg?w=640&h=640&fit=cover',
          width: 640,
        },
        original: {
          height: 1152,
          size: 153146,
          url: 'https://cdn.dashsocial.com/media/original/1779975914.055606433666.jpg',
          width: 896,
        },
        originalConverted: {
          height: 1152,
          size: 153247,
          url: 'https://cdn.dashsocial.com/media/full/1779975914.167844589836.jpeg',
          width: 896,
        },
        small: {
          height: 400,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9jZG4uZGFzaHNvY2lhbC5jb20vbWVkaWEvZnVsbC8xNzc5OTc1OTE0LjE2Nzg0NDU4OTgzNi5qcGVn.jpg?w=311&h=400',
          width: 311,
        },
        smallSquare: {
          height: 400,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9jZG4uZGFzaHNvY2lhbC5jb20vbWVkaWEvZnVsbC8xNzc5OTc1OTE0LjE2Nzg0NDU4OTgzNi5qcGVn.jpg?w=400&h=400&fit=cover',
          width: 400,
        },
      },
    },
  },
  tiktokVideo: {
    id: 389859070,
    brandId: MOCK_BRAND_ID,
    type: 'VIDEO',
    source: 'INSTAGRAM',
    sourceType: 'OWNED',
    createdAt: '2024-03-14T18:52:37+00:00',
    updatedAt: '2024-03-14T18:52:42+00:00',
    video: {
      duration: 7.8,
      frameRate: null,
      sizes: {
        original: {
          height: 720,
          size: 760944,
          url: 'https://cdn.dashsocial.com/media/original/1739372005.352089624221.mp4',
          width: 1280,
        },
        originalConverted: {
          height: 720,
          size: 761137,
          url: 'https://cdn.dashsocial.com/media/full/1739372033.17794630809.mp4',
          width: 1280,
        },
      },
      thumbnails: {
        mediumSquare: {
          height: 640,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9jZG4uZGFzaHNvY2lhbC5jb20vbWVkaWEvZnVsbC8xNzM5MzcyMDA2LjUzMjI5MzM4Mzk5NC5qcGVn.jpg?w=640&h=640&fit=cover',
          width: 640,
        },
        originalConverted: {
          height: 720,
          size: 73336,
          url: 'https://cdn.dashsocial.com/media/full/1739372006.532293383994.jpeg',
          width: 1280,
        },
        small: {
          height: 400,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9jZG4uZGFzaHNvY2lhbC5jb20vbWVkaWEvZnVsbC8xNzM5MzcyMDA2LjUzMjI5MzM4Mzk5NC5qcGVn.jpg?w=711&h=400',
          width: 711,
        },
        smallSquare: {
          height: 400,
          url: 'https://images.dashsocial.com/aHR0cHM6Ly9jZG4uZGFzaHNvY2lhbC5jb20vbWVkaWEvZnVsbC8xNzM5MzcyMDA2LjUzMjI5MzM4Mzk5NC5qcGVn.jpg?w=400&h=400&fit=cover',
          width: 400,
        },
      },
    },
  },
};

const POST_TEMPLATES = [
  {
    platform: 'instagram',
    postType: 'FEED',
    caption: 'Fluffy vibe check 🐱 #CatLife',
    media: [MEDIA.catImage],
    approvalStatus: 'APPROVED',
    dayOffset: 0,
    hour: 9,
  },
  {
    platform: 'instagram',
    postType: 'REEL',
    caption: 'Quando a vida felina é bem real 🐾',
    media: [MEDIA.instagramVideo],
    approvalStatus: 'PENDING',
    dayOffset: 1,
    hour: 12,
  },
  {
    platform: 'tiktok',
    postType: null,
    caption: 'Cats’ meeting in session! Who’s got the agenda? 🐱🌿',
    media: [MEDIA.tiktokVideo],
    approvalStatus: 'APPROVED',
    dayOffset: 2,
    hour: 15,
  },
  {
    platform: 'facebook',
    postType: 'POST',
    caption: 'Sunny mornings with a furry friend 🐾',
    media: [MEDIA.catImage],
    approvalStatus: null,
    dayOffset: 3,
    hour: 10,
  },
  {
    platform: 'pinterest',
    postType: null,
    caption: 'When your cat thinks they’re the head of the household 😹🐾 #CatBoss',
    media: [MEDIA.catImage],
    approvalStatus: 'APPROVED',
    dayOffset: 4,
    hour: 17,
  },
  {
    platform: 'instagram',
    postType: 'STORY',
    caption: 'cozy cat nap ✨',
    media: [MEDIA.instagramVideo],
    approvalStatus: 'PENDING',
    dayOffset: 5,
    hour: 19,
  },
];

// Spread the templates over a wide window so every prefetch interval (visible
// week/month ±1) has content and prev/next navigation stays interesting.
const WEEK_OFFSETS = [-5, -2, -1, 0, 1, 2, 5];

function buildMockPosts(): Array<Record<string, unknown>> {
  const startOfWeek = dayjs().startOf('week');
  return MOCK_POST_BRAND_IDS.flatMap((brandId, brandIndex) =>
    WEEK_OFFSETS.flatMap((weekOffset, weekIndex) =>
      POST_TEMPLATES.map((template, templateIndex) => {
        const timestamp = startOfWeek
          .add(weekOffset, 'week')
          .add(template.dayOffset, 'day')
          .hour(template.hour)
          .minute(15);
        return {
          id: 90000 + brandIndex * 10000 + weekIndex * 100 + templateIndex,
          brandId,
          platform: template.platform,
          postType: template.postType,
          caption: template.caption,
          media: template.media,
          mediaIds: template.media.map((media) => media.id),
          timestamp: timestamp.toISOString(),
          hasScheduledTime: true,
          status: 'SCHEDULED',
          approvalPolicy:
            template.approvalStatus === null ? 'APPROVAL_NOT_REQUIRED' : 'APPROVAL_REQUIRED',
          approvalStatus: template.approvalStatus,
          approvalRequests: [],
          numOfApprovalsRequired: template.approvalStatus === null ? 0 : 1,
          autoPublish: true,
          createdAt: timestamp.subtract(3, 'day').toISOString(),
          updatedAt: timestamp.subtract(1, 'day').toISOString(),
        };
      }),
    ),
  );
}

function respond<T>(data: T): Promise<{ data: T }> {
  return new Promise((resolve) => {
    setTimeout(() => resolve({ data }), MOCK_LATENCY_MS);
  });
}

export function mockFetchSharedCalendarPosts({
  start,
  end,
}: {
  sharedLinkId: string;
  start?: string;
  end?: string;
}): Promise<{ data: { data: PlatformScheduledPost[] } }> {
  const posts = buildMockPosts().filter(
    (post) =>
      (!start || !dayjs(post.timestamp as string).isBefore(dayjs(start))) &&
      (!end || !dayjs(post.timestamp as string).isAfter(dayjs(end))),
  );
  return respond({ data: posts as unknown as PlatformScheduledPost[] });
}
